import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  LayoutGrid,
  Target,
  GitBranch,
  Copy,
  Check,
  Terminal,
  BookOpen,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import { documentsApi } from '../api/documents';
import { projectBoardsApi, initiativesApi } from '../api/work';
import { PageLayout } from '../components/layout/PageLayout';

// ─── Copy Button ────────────────────────────────────────────
function CopyButton({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 text-neutral-400 hover:text-neutral-600 transition-colors ${className}`}
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// ─── Stat Card ──────────────────────────────────────────────
function StatCard({
  icon: Icon,
  label,
  value,
  loading,
}: {
  icon: typeof FileText;
  label: string;
  value: string | number;
  loading: boolean;
}) {
  return (
    <div className="card flex items-center gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-neutral-500" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-semibold text-neutral-900 tabular-nums">
          {loading ? (
            <div className="h-7 w-12 bg-neutral-200 rounded animate-pulse" />
          ) : (
            value
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────
const ProjectOverviewPage = () => {
  const { project, projectId, shortId, buildProjectUrl } = useProject();

  const [docCount, setDocCount] = useState<number | null>(null);
  const [boardCount, setBoardCount] = useState<number | null>(null);
  const [epicCount, setEpicCount] = useState<number | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    setStatsLoading(true);

    Promise.allSettled([
      documentsApi.list(projectId, { page_size: 1 }),
      projectBoardsApi.list(projectId),
      initiativesApi.list(projectId),
    ]).then(([docResult, boardResult, epicResult]) => {
      if (cancelled) return;
      if (docResult.status === 'fulfilled') setDocCount(docResult.value.total);
      if (boardResult.status === 'fulfilled') setBoardCount(boardResult.value.length);
      if (epicResult.status === 'fulfilled') setEpicCount(epicResult.value.length);
      setStatsLoading(false);
    });

    return () => { cancelled = true; };
  }, [projectId]);

  if (!project || !projectId) {
    return (
      <PageLayout variant="constrained">
        <div className="py-12 text-center text-neutral-500">
          No project selected.
        </div>
      </PageLayout>
    );
  }

  const statusColor: Record<string, string> = {
    setup: 'badge-warning',
    active: 'badge-success',
    paused: 'badge-info',
    archived: 'badge-error',
  };

  const typeColor: Record<string, string> = {
    service: 'bg-violet-100 text-violet-800',
    app: 'bg-amber-100 text-amber-800',
    library: 'bg-sky-100 text-sky-800',
  };

  return (
    <PageLayout variant="constrained">
      <div className="space-y-8 py-8 animate-fade-in">

        {/* ── Header ────────────────────────────── */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-neutral-900">{project.name}</h1>
            <span className={`badge ${typeColor[project.type] || 'badge-info'}`}>{project.type}</span>
            <span className={statusColor[project.status] || 'badge-info'}>{project.status}</span>
          </div>
          {project.description && (
            <p className="text-neutral-600 mb-3">{project.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-neutral-500">
            <span className="flex items-center gap-1.5">
              <code className="font-mono text-xs bg-neutral-100 border border-neutral-200 rounded px-1.5 py-0.5 text-neutral-700 select-all">
                {project.id}
              </code>
              <CopyButton text={project.id} />
            </span>
            <span>Created {new Date(project.createdAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* ── Quick Stats ───────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={FileText} label="Documents" value={docCount ?? '-'} loading={statsLoading} />
          <StatCard icon={LayoutGrid} label="Boards" value={boardCount ?? '-'} loading={statsLoading} />
          <StatCard icon={Target} label="Epics" value={epicCount ?? '-'} loading={statsLoading} />
          <StatCard
            icon={GitBranch}
            label="Branch"
            value={project.repository?.branch || '-'}
            loading={false}
          />
        </div>

        {/* ── Quick Links ───────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { to: buildProjectUrl('documents'), icon: BookOpen, label: 'Documentation', desc: 'Browse docs, specs, and guides' },
            { to: buildProjectUrl('boards'), icon: LayoutGrid, label: 'Boards', desc: 'Kanban boards and issues' },
            { to: '/settings/mcp', icon: Terminal, label: 'MCP Access', desc: 'Agent connection and tools' },
            { to: buildProjectUrl('settings'), icon: Settings, label: 'Settings', desc: 'Project configuration' },
          ].map(({ to, icon: Icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="card flex items-center gap-4 hover:border-neutral-300 hover:shadow transition-all group"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center group-hover:bg-neutral-200 transition-colors">
                <Icon className="w-5 h-5 text-neutral-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900">{label}</div>
                <div className="text-xs text-neutral-500">{desc}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition-colors" />
            </Link>
          ))}
        </div>

      </div>
    </PageLayout>
  );
};

export default ProjectOverviewPage;
